import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'

import { Button } from 'react-bootstrap'
import { PayloadAction } from '@reduxjs/toolkit'
import StyledSidebar from '../../../components/SidebarStyle'
import { currentUserSelector } from '../../auth/authSlice'
import TimeLeft from './TimeLeft'
import AttendeeList from './AttendeeList'
import { selectCurrentMeeting } from '../currentMeetingSlice'
import Loader from '../../../components/Loader'
import useFetchAttendees from '../../attendees/useFetchAttendees'
import { VirtualAttendee } from '../../../types/swagger'
import { _ } from '../../../utils/localization'
import RequestToJoinList from './RequestToJoinList'
import colors from '../../../constants/colors'
import { useReduxBus } from '../../../utils/reduxmiddleware/reduxEventBus'
import {
    CancelledVideocallRequest, RequestVideoAccess, WS
} from '../../videocall/videocallSlice'
import sendToUser from '../../websocket/sendToUser'
import Icon from '../../../components/Icon'
import { RootState } from '../../../store'

const S = {
    ContentTitleWrapper: styled.div`
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0 10px;
    `
}

const SidebarContentOfExpert = ({ attendees }: {attendees: VirtualAttendee[]}) => {
    const [attendeeIds, setAttendeeIds] = useState([])
    const dispatch = useDispatch()
    const videocall = useSelector((state: RootState) => state.videocall.current)

    useReduxBus((action: PayloadAction<RequestVideoAccess>) => {
        setAttendeeIds((a) => [...a, action.payload.user_requesting_access])
    }, WS.accessRequested.type)

    useReduxBus((action: PayloadAction<CancelledVideocallRequest>) => {
        setAttendeeIds(attendeeIds.filter((id) => id !== action.payload.user_cancelling_access_request))
    }, WS.accessRequestCanceled.type)

    const replyToRequest = (userId, accepted) => {
        dispatch(sendToUser({
            receiverId: userId,
            event: 'replied_videocall_access',
            data: {
                accepted,
            },
        }))
        setAttendeeIds(attendeeIds.filter((id) => id !== userId))
    }

    const acceptAllRequests = () => {
        attendeeIds.forEach((id) => replyToRequest(id, true))
        // replyToRequest doesn't remove all IDs
        setAttendeeIds([])
    }

    const muteUsers = () => {
        videocall.users.forEach((user) => {
            dispatch(sendToUser({
                receiverId: user,
                event: 'host_muted_videocall',
            }))
        })
    }

    return (
        <StyledSidebar.SidebarContent>
            {attendeeIds.length > 0
            && (
                <div>
                    <S.ContentTitleWrapper>
                        <StyledSidebar.SidebarContentTitle>{_('meeting.sidebar.requests_to_join')}</StyledSidebar.SidebarContentTitle>
                        <Button
                            variant="light"
                            size="sm"
                            style={{ border: colors.outlineButton, padding: '2px 5px' }}
                            onClick={() => acceptAllRequests()}
                        >
                            {_('meeting.sidebar.accept_all')}
                        </Button>
                    </S.ContentTitleWrapper>
                    <RequestToJoinList attendeeIds={attendeeIds} replyToRequest={replyToRequest} />
                </div>
            )}

            <S.ContentTitleWrapper>
                <StyledSidebar.SidebarContentTitle>{_('meeting.sidebar.attendees')}</StyledSidebar.SidebarContentTitle>
                <Icon
                    icon="volumeOff"
                    color={colors.regentGray}
                    onClick={() => muteUsers()}
                />
            </S.ContentTitleWrapper>
            <AttendeeList attendees={attendees} currentUserIsAdmin />
        </StyledSidebar.SidebarContent>
    )
}
const SidebarContentOfAttendee = ({ attendees }: {attendees: VirtualAttendee[]}) => (
    <StyledSidebar.SidebarContent>
        <StyledSidebar.SidebarContentTitle>{_('meeting.sidebar.attendees')}</StyledSidebar.SidebarContentTitle>
        <AttendeeList attendees={attendees} />
    </StyledSidebar.SidebarContent>
)

const MeetingSidebar = () => {
    const currentUser = useSelector(currentUserSelector())
    const currentMeeting = useSelector(selectCurrentMeeting)
    const attendees = useFetchAttendees(currentMeeting?.users)

    if (!currentMeeting) {
        return <Loader />
    }

    const SidebarContent = currentMeeting?.host_id === currentUser.id
        ? SidebarContentOfExpert
        : SidebarContentOfAttendee

    return (
        <StyledSidebar.Sidebar>
            <StyledSidebar.SideBarTop>
                <StyledSidebar.SideBarTitle>{_('meeting.sidebar.title')}</StyledSidebar.SideBarTitle>
                {currentMeeting && <TimeLeft end_date={currentMeeting.ends_at} />}
            </StyledSidebar.SideBarTop>

            <SidebarContent attendees={attendees} />
        </StyledSidebar.Sidebar>
    )
}

export default MeetingSidebar
